import { useEffect } from 'react';

import type { SiteFontFamily } from '@/types/database';

// Legibility-based classification, not a marketing adjective — lets the
// Fonts screen group/label options by what they actually are (a System Sans
// vs. a Display Serif behave very differently at body-text sizes) instead of
// subjective words like "bold & confident."
export type FontClassification =
  | 'System Sans'
  | 'Humanist Sans'
  | 'Geometric Sans'
  | 'Grotesque Sans'
  | 'Traditional Serif'
  | 'Display Serif';

export interface FontCatalogEntry {
  label: string;
  classification: FontClassification;
  // One short, factual line about how/where this face reads well — not a
  // marketing adjective (see FontClassification above for the same reasoning).
  legibilityNote: string;
  cssStack: string;
  // Google Fonts family name + weights to request — omitted for the two
  // system-safe options, which need no network request at all.
  googleFont?: { family: string; weights: number[] };
}

// A small, curated set — every option here is guaranteed to load and
// render correctly, unlike an open "type any Google Font name" field.
export const FONT_CATALOG: Record<SiteFontFamily, FontCatalogEntry> = {
  system: {
    label: 'System',
    classification: 'System Sans',
    legibilityNote: "Matches each visitor's own device font — instantly familiar, no load time.",
    cssStack: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  },
  georgia: {
    label: 'Georgia',
    classification: 'Traditional Serif',
    legibilityNote: 'Classic book serif — formal, holds up well at small body-text sizes.',
    cssStack: 'Georgia, "Times New Roman", Times, serif',
  },
  marcellus: {
    label: 'Marcellus',
    classification: 'Display Serif',
    legibilityNote: 'High-contrast, elegant strokes — best reserved for short headings, not paragraphs.',
    cssStack: '"Marcellus", Georgia, serif',
    googleFont: { family: 'Marcellus', weights: [400] },
  },
  'playfair-display': {
    label: 'Playfair Display',
    classification: 'Display Serif',
    legibilityNote: 'Bold editorial serif with dramatic thick/thin strokes — strong at large heading sizes.',
    cssStack: '"Playfair Display", Georgia, serif',
    googleFont: { family: 'Playfair Display', weights: [400, 600, 700] },
  },
  poppins: {
    label: 'Poppins',
    classification: 'Geometric Sans',
    legibilityNote: 'Rounded, geometric letterforms — reads confidently at large sizes.',
    cssStack: '"Poppins", "Segoe UI", sans-serif',
    googleFont: { family: 'Poppins', weights: [400, 500, 600, 700] },
  },
  'open-sans': {
    label: 'Open Sans',
    classification: 'Humanist Sans',
    legibilityNote: 'Open, warm letterforms designed for long-form reading on screens.',
    cssStack: '"Open Sans", "Segoe UI", sans-serif',
    googleFont: { family: 'Open Sans', weights: [400, 600, 700] },
  },
  inter: {
    label: 'Inter',
    classification: 'Grotesque Sans',
    legibilityNote: 'Neutral, screen-optimized letterforms — stays clean from caption to headline sizes.',
    cssStack: '"Inter", "Segoe UI", sans-serif',
    googleFont: { family: 'Inter', weights: [400, 500, 600, 700] },
  },
  roboto: {
    label: 'Roboto',
    classification: 'Grotesque Sans',
    legibilityNote: 'Neutral and versatile — reads consistently across phone, tablet, and desktop.',
    cssStack: '"Roboto", "Segoe UI", sans-serif',
    googleFont: { family: 'Roboto', weights: [400, 500, 700] },
  },
  lato: {
    label: 'Lato',
    classification: 'Humanist Sans',
    legibilityNote: 'Warm, semi-rounded letterforms — friendly without sacrificing paragraph legibility.',
    cssStack: '"Lato", "Segoe UI", sans-serif',
    googleFont: { family: 'Lato', weights: [400, 700] },
  },
};

export const SITE_FONT_OPTIONS: { value: SiteFontFamily; label: string; classification: FontClassification; legibilityNote: string }[] = (
  Object.keys(FONT_CATALOG) as SiteFontFamily[]
).map((value) => ({
  value,
  label: FONT_CATALOG[value].label,
  classification: FONT_CATALOG[value].classification,
  legibilityNote: FONT_CATALOG[value].legibilityNote,
}));

export function fontCssStack(family: SiteFontFamily): string {
  return FONT_CATALOG[family]?.cssStack ?? FONT_CATALOG.system.cssStack;
}

function googleFontsHref(families: SiteFontFamily[]): string | null {
  const entries = Array.from(new Set(families))
    .map((family) => FONT_CATALOG[family]?.googleFont)
    .filter((entry): entry is NonNullable<typeof entry> => Boolean(entry));
  if (entries.length === 0) return null;

  const familyParams = entries
    .map((entry) => `family=${encodeURIComponent(entry.family)}:wght@${entry.weights.join(';')}`)
    .join('&');
  return `https://fonts.googleapis.com/css2?${familyParams}&display=swap`;
}

// Injects a single <link> covering exactly the web fonts a site's theme
// currently uses — not the whole catalog — so a unit that never picks a
// Google Font never pays for one, and switching fonts doesn't accumulate
// stale <link> tags (the id is stable, the href is replaced in place).
export function useGoogleFonts(families: SiteFontFamily[]): void {
  const href = googleFontsHref(families);

  useEffect(() => {
    const linkId = 'site-theme-google-fonts';
    if (!href) {
      document.getElementById(linkId)?.remove();
      return;
    }

    let link = document.getElementById(linkId) as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.id = linkId;
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }
    link.href = href;
  }, [href]);
}
